// Round 1's sounds, synthesized with Web Audio. Round 2 (2026-11-05) keeps
// them as named voices beside the recorded files (lib/sound/catalog.ts).
//
// Every voice is a few sine/triangle partials with a 4 ms attack and an
// exponential decay — a struck, rounded sound, never a buzzer — written into
// `out`, which the engine sends to a short room reverb and the master gain.
export type Voice = (ctx: BaseAudioContext, out: AudioNode, t: number) => void

interface ToneSpec {
  freq: number
  at: number
  decay: number
  gain: number
  type?: OscillatorType
  /** [frequency ratio, relative amplitude] above the fundamental */
  partials?: readonly (readonly [number, number])[]
  /** end frequency of a short glide, Hz */
  glideTo?: number
  lowpass?: number
}

const ATTACK = 0.004
const SILENT = 0.0001

function tone(ctx: BaseAudioContext, out: AudioNode, t0: number, spec: ToneSpec): void {
  const t = t0 + spec.at
  const env = ctx.createGain()
  env.gain.setValueAtTime(SILENT, t)
  env.gain.linearRampToValueAtTime(spec.gain, t + ATTACK)
  env.gain.exponentialRampToValueAtTime(SILENT, t + ATTACK + spec.decay)
  let sink: AudioNode = env
  if (spec.lowpass) {
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = spec.lowpass
    env.connect(filter)
    sink = filter
  }
  sink.connect(out)
  const stopAt = t + ATTACK + spec.decay + 0.05
  for (const [ratio, amp] of [[1, 1] as const, ...(spec.partials ?? [])]) {
    const osc = ctx.createOscillator()
    osc.type = spec.type ?? 'sine'
    osc.frequency.setValueAtTime(spec.freq * ratio, t)
    if (spec.glideTo) {
      osc.frequency.exponentialRampToValueAtTime(
        spec.glideTo * ratio,
        t + Math.min(0.12, spec.decay)
      )
    }
    const partGain = ctx.createGain()
    partGain.gain.value = amp
    osc.connect(partGain).connect(env)
    osc.start(t)
    osc.stop(stopAt)
  }
}

function noiseTick(
  ctx: BaseAudioContext,
  out: AudioNode,
  t: number,
  dur: number,
  gain: number,
  highpass: number
): void {
  const length = Math.max(1, Math.floor(ctx.sampleRate * dur))
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2
  const src = ctx.createBufferSource()
  src.buffer = buffer
  const filter = ctx.createBiquadFilter()
  filter.type = 'highpass'
  filter.frequency.value = highpass
  const env = ctx.createGain()
  env.gain.value = gain
  src.connect(filter).connect(env).connect(out)
  src.start(t)
}

function fmBell(
  ctx: BaseAudioContext,
  out: AudioNode,
  t0: number,
  at: number,
  freq: number,
  decay: number,
  gain: number
): void {
  const t = t0 + at
  const carrier = ctx.createOscillator()
  const modulator = ctx.createOscillator()
  const modGain = ctx.createGain()
  const env = ctx.createGain()
  carrier.frequency.value = freq
  modulator.frequency.value = freq * 3.5
  modGain.gain.setValueAtTime(freq * 2.2, t)
  modGain.gain.exponentialRampToValueAtTime(freq * 0.05, t + decay)
  env.gain.setValueAtTime(SILENT, t)
  env.gain.linearRampToValueAtTime(gain, t + ATTACK)
  env.gain.exponentialRampToValueAtTime(SILENT, t + ATTACK + decay)
  modulator.connect(modGain).connect(carrier.frequency)
  carrier.connect(env).connect(out)
  for (const osc of [carrier, modulator]) {
    osc.start(t)
    osc.stop(t + ATTACK + decay + 0.05)
  }
}

const BELL = [
  [2, 0.18],
  [3, 0.06],
] as const
const MARIMBA = [[4, 0.1]] as const

export const SYNTH_VOICES = {
  // a crisp dry tick
  dryTick: (ctx, out, t) => {
    noiseTick(ctx, out, t, 0.018, 0.16, 2500)
    tone(ctx, out, t, { freq: 1900, glideTo: 1300, at: 0, decay: 0.025, gain: 0.07 })
  },
  // a soft wooden tock
  woodenTock: (ctx, out, t) => {
    tone(ctx, out, t, { freq: 900, glideTo: 620, at: 0, decay: 0.04, gain: 0.13 })
    noiseTick(ctx, out, t, 0.01, 0.05, 4000)
  },
  // two bell notes up a fifth (A5 -> E6)
  bellsFifth: (ctx, out, t) => {
    tone(ctx, out, t, { freq: 880, at: 0, decay: 0.45, gain: 0.2, partials: BELL })
    tone(ctx, out, t, { freq: 1318.5, at: 0.07, decay: 0.7, gain: 0.18, partials: BELL })
  },
  // a soft marimba pair (G5 -> D6)
  marimbaPair: (ctx, out, t) => {
    tone(ctx, out, t, {
      freq: 784,
      at: 0,
      decay: 0.3,
      gain: 0.22,
      type: 'triangle',
      partials: MARIMBA,
    })
    tone(ctx, out, t, {
      freq: 1174.7,
      at: 0.06,
      decay: 0.5,
      gain: 0.2,
      type: 'triangle',
      partials: MARIMBA,
    })
  },
  // two muted notes down (G4 -> Eb4), never harsh
  mutedDown: (ctx, out, t) => {
    tone(ctx, out, t, {
      freq: 392,
      at: 0,
      decay: 0.28,
      gain: 0.2,
      type: 'triangle',
      lowpass: 1500,
    })
    tone(ctx, out, t, {
      freq: 311.1,
      at: 0.095,
      decay: 0.42,
      gain: 0.2,
      type: 'triangle',
      lowpass: 1500,
    })
  },
  // one low falling knock
  lowKnock: (ctx, out, t) => {
    tone(ctx, out, t, {
      freq: 220,
      glideTo: 180,
      at: 0,
      decay: 0.35,
      gain: 0.26,
      partials: [[2.7, 0.15]],
    })
  },
  // a quick rising bell arpeggio (C6 E6 G6)
  bellArpeggio: (ctx, out, t) => {
    tone(ctx, out, t, { freq: 1046.5, at: 0, decay: 0.35, gain: 0.16, partials: BELL })
    tone(ctx, out, t, { freq: 1318.5, at: 0.055, decay: 0.4, gain: 0.16, partials: BELL })
    tone(ctx, out, t, { freq: 1568, at: 0.11, decay: 0.8, gain: 0.17, partials: BELL })
  },
  // a bright two-step coin (G6 -> C7)
  coinTwoStep: (ctx, out, t) => {
    tone(ctx, out, t, {
      freq: 1568,
      at: 0,
      decay: 0.18,
      gain: 0.14,
      type: 'triangle',
      partials: [[3, 0.25]],
    })
    tone(ctx, out, t, {
      freq: 2093,
      at: 0.06,
      decay: 0.55,
      gain: 0.14,
      type: 'triangle',
      partials: [[3, 0.25]],
    })
  },
  // two struck FM bells (B5), clearly "look"
  fmBells: (ctx, out, t) => {
    fmBell(ctx, out, t, 0, 987.8, 0.7, 0.18)
    fmBell(ctx, out, t, 0.15, 987.8, 0.9, 0.16)
  },
  // a two-tone chime falling (D6 -> A5)
  fallingChime: (ctx, out, t) => {
    tone(ctx, out, t, { freq: 1174.7, at: 0, decay: 0.6, gain: 0.18, partials: BELL })
    tone(ctx, out, t, { freq: 880, at: 0.18, decay: 0.9, gain: 0.18, partials: BELL })
  },
} satisfies Record<string, Voice>

export type SynthVoice = keyof typeof SYNTH_VOICES
